<template>
  <div class="container-fluid p-4">
    <div class="box">
      <!-- Header Section: Title and DateRangePicker -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="d-flex align-items-center" :class="{ 'mb-3': isMobile }">
            <h2 class="mb-0">{{ $t('events.titleList') }}</h2>
            <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('events.helpTooltip')">
              <i class="bi bi-question-circle ms-2 help-icon"></i>
            </span>
          </div>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-12 d-flex align-items-center gap-3 flex-wrap">
          <!-- DateRangePicker -->
          <DateRangePicker
            v-model="dateRange"
            alignment="left"
            :max-days="365"
            @update:modelValue="handleDateRangeChange"
          />
          <a href="#" class="reset-link" @click.prevent="resetDateRange">
            <i class="bi bi-arrow-counterclockwise me-1"></i>{{ $t('common.resetView') }}
          </a>
        </div>
      </div>

      <!-- Table with horizontal scroll -->
      <div class="table-wrapper">
        <div v-if="page" class="table-scroll">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ $t('events.eventName') }}</th>
                <th>{{ $t('events.capacity') }}</th>
                <th>{{ $t('events.isOnline') }}</th>
                <th>{{ $t('events.organizer') }}</th>
                <th>{{ $t('events.eventDate') }}</th>
                <th>{{ $t('events.fee') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="event in page.items"
                :key="event.id"
                @click="viewEvent(event.id)"
                class="cursor-pointer"
              >
                <td>{{ event.title }}</td>
                <td>{{ event.capacity > 0 ? event.capacity : '-' }}</td>
                <td>{{ event.isOnline ? '◯' : '-' }}</td>
                <td>{{ formatOrganizerText(event) }}</td>
                <td>{{ event.dateRangeDisplayText }}</td>
                <td class="text-end">{{ event.feeDisplayText }}</td>
              </tr>
              <tr v-if="page.items.length === 0">
                <td colspan="6" class="text-center text-muted py-4">
                  {{ $t('events.noData') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ $t('events.loading') }}</span>
          </div>
        </div>

        <!-- Pagination -->
        <SimplePagination
          v-if="page"
          :page="page"
          @change="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Popover } from 'bootstrap';
import EventFilter from '../../../filters/user/EventFilter';
import usePagination from '../../../composables/usePagination';
import SimplePagination from '../../../components/SimplePagination.vue';
import DateRangePicker from '../../../components/user/dashboard/DateRangePicker.vue';
import { useIsMobile } from '../../../utils/DeviceUtil';
import { useSharedDateRange, defaultDateRange } from '../../../composables/useSharedDateRange';
import { colors } from '../../../../consts/colors';

const { t } = useI18n();
const { isMobile } = useIsMobile();
const route = useRoute();
const router = useRouter();

// Parse date string (YYYY-MM-DD) as local timezone
const parseDateString = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Shared across dashboard, events and accommodations; explicit query
// params (e.g. the dashboard's event list link) take precedence.
const { dateRange } = useSharedDateRange();

const queryStartDate = route.query.start_date;
const queryEndDate = route.query.end_date;
if (queryStartDate && queryEndDate) {
  // Parse dates from query params as local timezone (format: YYYY-MM-DD)
  const startDate = parseDateString(queryStartDate);
  const endDate = parseDateString(queryEndDate);

  if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    dateRange.value = { startDate, endDate };
  }
}

// Initialize filter with date range
const filter = ref(new EventFilter());
filter.value.setDateRange(dateRange.value.startDate, dateRange.value.endDate);

const { page, search, changePage } = usePagination(filter.value);

/**
 * Handle date range change
 */
const handleDateRangeChange = (newDateRange) => {
  filter.value.setDateRange(newDateRange.startDate, newDateRange.endDate);
  search();
};

/**
 * 期間をデフォルト(今日〜7日後)に戻す
 */
const resetDateRange = () => {
  dateRange.value = defaultDateRange();
  handleDateRangeChange(dateRange.value);
};

/**
 * Format organizer display text with i18n support
 * e.g., "東川町" or "東川町 他2事業者"
 */
const formatOrganizerText = (event) => {
  if (event.hasAdditionalOrganizers) {
    return `${event.primaryOrganizerName} ${t('events.additionalOrganizers', { count: event.additionalOrganizersCount })}`;
  }
  return event.primaryOrganizerName;
};

/**
 * View event details
 * @param {string} id - Event ID
 */
const viewEvent = (id) => {
  router.push({ name: 'events.show', params: { id } });
};

// Initialize Bootstrap popovers
onMounted(() => {
  // Set CSS variables for colors
  const root = document.documentElement;
  root.style.setProperty('--color-text-primary', colors.textPrimary);
  root.style.setProperty('--color-white', colors.white);

  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].map(popoverTriggerEl => new Popover(popoverTriggerEl, {
    customClass: 'custom-popover'
  }));
});
</script>

<style lang="scss" scoped>
.container-fluid {
  max-width: 1400px;
}

.reset-link {
  font-size: 13px;
  color: v-bind('colors.btnBgColorPrimary');
  text-decoration: none;
  white-space: nowrap;
}

h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
}

.help-icon {
  color: v-bind('colors.gray[400]');
  font-size: 1rem;
  cursor: help;

  &:hover {
    color: v-bind('colors.gray[600]');
  }
}

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
}

// Table styles
.table {
  margin-bottom: 0;
  font-size: 0.95rem;

  thead th {
    border-bottom: 2px solid v-bind('colors.gray[200]');
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    background-color: v-bind('colors.gray[50]');
    padding: 0.75rem;
    white-space: nowrap;

    // Column widths
    &:nth-child(1) { min-width: 300px; } // イベント名
    &:nth-child(2) { min-width: 80px; }  // 定員
    &:nth-child(3) { min-width: 100px; } // オンライン
    &:nth-child(4) { min-width: 200px; } // 主催
    &:nth-child(5) { min-width: 180px; } // 開催日
    &:nth-child(6) { min-width: 120px; } // 参加費
  }

  tbody {
    td {
      padding: 0.75rem;
      color: v-bind('colors.textSecondary');
      vertical-align: middle;
    }

    .cursor-pointer {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: v-bind('colors.bgHover');
      }
    }
  }

  .text-muted {
    color: v-bind('colors.gray[500]') !important;
  }
}

// Loading spinner
.spinner-border {
  color: v-bind('colors.blue[500]');
}

// Table wrapper with horizontal scroll and pagination
.table-wrapper {
  position: relative;
}

.table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: v-bind('colors.gray[100]');
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: v-bind('colors.gray[400]');
    border-radius: 4px;

    &:hover {
      background: v-bind('colors.gray[500]');
    }
  }
}

// Ensure table doesn't collapse
.table-scroll table {
  min-width: 900px;
  margin-bottom: 0;
}
</style>

<style lang="scss">
// Global styles for custom popover (not scoped)
.custom-popover {
  .popover-body {
    background-color: var(--color-text-primary);
    color: var(--color-white);
  }

  &.bs-popover-auto[data-popper-placement^="top"] > .popover-arrow::after,
  &.bs-popover-top > .popover-arrow::after {
    border-top-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="bottom"] > .popover-arrow::after,
  &.bs-popover-bottom > .popover-arrow::after {
    border-bottom-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="left"] > .popover-arrow::after,
  &.bs-popover-start > .popover-arrow::after {
    border-left-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="right"] > .popover-arrow::after,
  &.bs-popover-end > .popover-arrow::after {
    border-right-color: var(--color-text-primary);
  }
}
</style>
